import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import xss from 'xss';
import {
    Banner,
    BannerDescription,
    BannerEyebrow,
    BannerImage,
    BannerImageContainer,
    BannerLinks,
    BannerText,
    BannerTextContainer,
    BannerTitle,
    Box,
    Icon,
    IconButton,
} from '@vp/swan';
import { useStyles } from '@vp/ubik-context';
import EditWrapper from '../../../../components/EditWrapper/EditWrapper';
import { sanitiseLocale } from '../../../../i18n/config';
import { useTrackNavigationClicked } from '../../../../utils/analytics';
import { logomakerCtaPathByLocale } from '../../../../utils/urls';
import LogomakerCTA from '../LogomakerCTA/LogomakerCTA';
import styles, { stylesheet } from './Hero.module.scss';

const Hero = () => {
    useStyles(stylesheet);
    const { t, i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);
    const videoPlayerRef = useRef<HTMLVideoElement>(null);
    const [videoPlaying, setVideoPlaying] = useState(true);
    const displayBritishEnglish = ['en-GB', 'en-IE', 'en-AU', 'en-NZ', 'en-SG'].includes(locale);
    const cloudinaryURL =
        'https://cms.cloudinary.vpsvc.com/video/upload/v1684152665/vista-logos-pages';

    // This is a temporary solution as these assets will eventually be pulled from Contentful.
    // However, the Banner contentful entry needs to first be updated in order to allow for videos.
    let mp4VideoURL = `${cloudinaryURL}/en-US/logomaker/US_Logomaker_Bella_1080x1080_H265.mp4`;
    let webmVideoURL = `${cloudinaryURL}/en-US/logomaker/US_Logomaker_Bella_1080x1080.webm`;

    if (locale.includes('de')) {
        mp4VideoURL = `${cloudinaryURL}/de-DE/logomaker/DE_Logomaker_Bella_1080x1080_H265.mp4`;
        webmVideoURL = `${cloudinaryURL}/de-DE/logomaker/DE_Logomaker_Bella_1080x1080.webm`;
    } else if (locale.includes('fr')) {
        mp4VideoURL = `${cloudinaryURL}/fr-FR/logomaker/FR_Logomaker_Bella_1080x1080_H265.mp4`;
        webmVideoURL = `${cloudinaryURL}/fr-FR/logomaker/FR_Logomaker_Bella_1080x1080.webm`;
    } else if (locale.includes('nl')) {
        mp4VideoURL = `${cloudinaryURL}/nl-BE/logomaker/nl-BE_nl-NL-Logomaker-1080x1080_H.265_H.265.mp4`;
        webmVideoURL = `${cloudinaryURL}/nl-BE/logomaker/nl-BE_nl-NL-Logomaker-1080x1080_H.265.webm`;
    } else if (locale.includes('it')) {
        mp4VideoURL = `${cloudinaryURL}/it-IT/logomaker/it-IT-Logomaker-1080x1080_H.265.mp4`;
        webmVideoURL = `${cloudinaryURL}/it-IT/logomaker/it-IT-Logomaker-1080x1080.webm`;
    } else if (locale === 'es-ES') {
        mp4VideoURL = `${cloudinaryURL}/es-ES/logomaker/es-ES-Logomaker-1080x1080_H.265.mp4`;
        webmVideoURL = `${cloudinaryURL}/es-ES/logomaker/es-ES-Logomaker-1080x1080.webm`;
    } else if (locale === 'es-US') {
        mp4VideoURL = `${cloudinaryURL}/es-US/logomaker/es-EU-Logomaker-1080x1080_H.265.mp4`;
        webmVideoURL = `${cloudinaryURL}/es-US/logomaker/es-EU-Logomaker-1080x1080.webm`;
    } else if (locale === 'pt-PT') {
        mp4VideoURL = `${cloudinaryURL}/pt-PT/logomaker/pt-PT-Logomaker-1080x1080_H.265.mp4`;
        webmVideoURL = `${cloudinaryURL}/pt-PT/logomaker/pt-PT-Logomaker-1080x1080.webm`;
    } else if (locale === 'nb-NO') {
        mp4VideoURL = `${cloudinaryURL}/nb-NO/logomaker/nb-NO-Logomaker-1080x1080_H.265.mp4`;
        webmVideoURL = `${cloudinaryURL}/nb-NO/logomaker/nb-NO-Logomaker-1080x1080.webm`;
    } else if (locale === 'da-DK') {
        mp4VideoURL = `${cloudinaryURL}/da-DK/logomaker/da-DK-Logomaker-1080x1080_H.265.mp4`;
        webmVideoURL = `${cloudinaryURL}/da-DK/logomaker/da-DK-Logomaker-1080x1080.webm`;
    } else if (locale === 'sv-SE') {
        mp4VideoURL = `${cloudinaryURL}/sv-SE/logomaker/sv-SE-Logomaker-1080x1080_H.265.mp4`;
        webmVideoURL = `${cloudinaryURL}/sv-SE/logomaker/sv-SE-Logomaker-1080x1080.webm`;
    } else if (locale === 'fi-FI') {
        mp4VideoURL = `${cloudinaryURL}/fi-FI/logomaker/fi-FI-Logomaker-1080x1080_H.265.mp4`;
        webmVideoURL = `${cloudinaryURL}/fi-FI/logomaker/fi-FI-Logomaker-1080x1080.webm`;
    } else if (displayBritishEnglish) {
        mp4VideoURL = `${cloudinaryURL}/en-UK/logomaker/UK_Logomaker_Bella_1080x1080_H265.mp4`;
        webmVideoURL = `${cloudinaryURL}/en-UK/logomaker/UK_Logomaker_Bella_1080x1080.webm`;
    }

    const logomakerCTARef = useTrackNavigationClicked(
        'Design Services',
        'Hero Banner CTA',
        logomakerCtaPathByLocale(locale),
        'logoMaker',
    );

    return (
        <EditWrapper url={t('Logomaker landing page > Hero.editUrl')}>
            <Banner bgc="warm-white">
                <BannerTextContainer>
                    <BannerText>
                        <BannerEyebrow as="div">
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: xss(
                                        marked.parse(
                                            t('Logomaker landing page > Hero.eyebrow.value'),
                                        ) as string,
                                    ),
                                }}
                            />
                        </BannerEyebrow>
                        <BannerTitle as="h1">
                            {t('Logomaker landing page > Hero.title.value')}
                        </BannerTitle>
                        <BannerDescription as="p">
                            {t('Logomaker landing page > Hero.description.value')}
                        </BannerDescription>
                        <BannerLinks>
                            <LogomakerCTA ctaRef={logomakerCTARef} />
                        </BannerLinks>
                    </BannerText>
                </BannerTextContainer>
                <BannerImageContainer>
                    <BannerImage className={styles.bannerVideo}>
                        <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            aria-label={t('Logomaker landing page > Hero.imageSrc.altTextOverride')}
                            ref={videoPlayerRef}
                        >
                            {/* TODO: Replace video src with contentful data once banner updated */}
                            <source src={mp4VideoURL} type="video/mp4;codecs=hvc1" />
                            <source src={webmVideoURL} type="video/webm" />
                        </video>
                    </BannerImage>
                    <Box className={styles.bannerVideoControls}>
                        <IconButton
                            aria-label={
                                videoPlaying
                                    ? t(
                                          'Logomaker landing page > Hero > Video Controls > Pause Button Aria Label.value',
                                      )
                                    : t(
                                          'Logomaker landing page > Hero > Video Controls > Play Button Aria Label.value',
                                      )
                            }
                            tooltip={
                                videoPlaying
                                    ? t(
                                          'Logomaker landing page > Hero > Video Controls > Pause Button Title.value',
                                      )
                                    : t(
                                          'Logomaker landing page > Hero > Video Controls > Play Button Title.value',
                                      )
                            }
                            onClick={() => {
                                if (videoPlayerRef && videoPlayerRef.current) {
                                    if (videoPlaying) {
                                        videoPlayerRef.current.pause();
                                    } else {
                                        videoPlayerRef.current.play();
                                    }
                                    setVideoPlaying(!videoPlaying);
                                }
                            }}
                        >
                            {videoPlaying ? <Icon iconType="pause" /> : <Icon iconType="play" />}
                        </IconButton>
                    </Box>
                </BannerImageContainer>
            </Banner>
        </EditWrapper>
    );
};

export default Hero;
